import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useState } from 'react';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { Image, TouchableOpacity, View } from 'react-native';
import { Text } from '@/components/Text';
import { ClampableText } from '@/components/ClampableText';
import { deletePatient } from '@/api/services/patient';
import AVATARS from '@/assets/avatars';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { Patient } from '@/stores/store.types';
import colors from '@/config/colors';
import moment from 'moment';
import { getInitials } from '@/utils/stringHelpers';
import { styles } from './styles';

type PatientProfileHeaderProps = {
  _id: string;
  avatar?: string[];
  fullName: string;
  biography: string;
  diagnosis: string;
  code: string;
  gender: string;
  created: string;
  fetchPatients: (
    selectedPatient: Patient | null,
    patientsParameter: Patient[] | null,
  ) => Promise<void>;
  navigation: StackNavigationProp<TherapistStackParamList>;
};

function formatCreatedDate(dateString: string): string {
  const date = moment(dateString);
  if (!date.isValid()) {
    return '';
  }
  return `erstellt am ${date.format('DD.MM.YYYY')}`;
}

export const PatientProfileHeader = ({
  _id,
  avatar,
  fullName,
  biography,
  diagnosis,
  code,
  gender,
  created,
  fetchPatients,
  navigation,
}: PatientProfileHeaderProps) => {
  const avatarKey = avatar?.[0]?.split('/').pop()?.split('.')[0] as
    | keyof typeof AVATARS
    | undefined;
  const avatarSource = avatarKey ? AVATARS[avatarKey] : undefined;
  const { isTherapistDemo } = useStoreState((store) => ({
    isTherapistDemo: store.isTherapistDemo,
  }));
  const { setGlobalPopupState } = useStoreActions((actions) => ({
    setGlobalPopupState: actions.setGlobalPopupState,
  }));

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isInfoCardExpanded, setIsInfoCardExpanded] = useState(false);
  const [isDiagnosisTruncated, setIsDiagnosisTruncated] = useState(false);
  const [isBiographyTruncated, setIsBiographyTruncated] = useState(false);

  const canExpand = isDiagnosisTruncated || isBiographyTruncated;
  const hasCode = !!code;
  const createdLabel = created ? formatCreatedDate(created) : '';

  const handleDeleteClick = useCallback(async () => {
    try {
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        setShowDeleteModal(true);
      } else {
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    } catch (error) {
      logError('handleDeleteClick in PatientProfileHeader', error);
    }
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await deletePatient(_id);
      setShowDeleteModal(false);
      await fetchPatients(null, null);
      navigation.navigate('Home');
    } catch (error) {
      logError('Could not delete patient', error);
    }
  }, [_id, fetchPatients, navigation]);

  const handleEditClick = useCallback(async () => {
    try {
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        navigation.push('AddPatientScreen', { _id });
      } else {
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    } catch (error) {
      logError('Could not edit patient without internet connection', error);
    }
  }, [_id]);

  return (
    <>
      <Modal
        isVisible={showDeleteModal}
        cancelActionButton={
          <Button
            label="Abbrechen"
            look="secondary"
            onPress={handleDeleteCancel}
          />
        }
        confirmActionButton={
          <Button label="Löschen" look="danger" onPress={handleDeleteConfirm} />
        }
        onClose={() => setShowDeleteModal(false)}
        title="Patienten-Profil löschen"
      >
        <Text style={{ fontSize: 20 }}>
          Möchten Sie das Patienten-Profil {fullName} wirklich löschen? Das
          Löschen ist unwiderruflich.
        </Text>
      </Modal>

      <View style={styles.container}>
        <View style={styles.topRow}>
          <View style={styles.avatarAndName}>
            {avatarSource ? (
              <Image style={styles.avatar} source={avatarSource} />
            ) : (
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>{getInitials(fullName)}</Text>
              </View>
            )}
            <View style={styles.nameColumn}>
              <Text
                style={styles.nameText}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {fullName}
              </Text>
              <View style={styles.metaRow}>
                {!!gender && (
                  <>
                    <Text style={styles.metaText}>
                      {gender.length > 8 ? `${gender.slice(0, 8)}…` : gender}
                    </Text>
                    <Text style={styles.metaSeparator}> | </Text>
                  </>
                )}
                <View style={styles.codeRow}>
                  <View
                    style={[
                      styles.codeDot,
                      {
                        backgroundColor: hasCode
                          ? colors.newUI.therapist.brandGreen
                          : colors.newUI.therapist.red50,
                      },
                    ]}
                  />
                  <Text style={styles.metaText}>
                    {hasCode ? code : 'Kein Patient:innencode'}
                  </Text>
                </View>
              </View>
              {!!createdLabel && (
                <Text style={styles.createdText}>{createdLabel}</Text>
              )}
            </View>
          </View>

          {!isTherapistDemo && (
            <View style={styles.actions}>
              <Button
                look="tertiary"
                label="Bearbeiten"
                icon="edit"
                onPress={handleEditClick}
              />
              <Button
                look="dangerTertiary"
                label="Löschen"
                icon="trashAlt"
                onPress={handleDeleteClick}
              />
            </View>
          )}
        </View>

        {(!!biography || !!diagnosis) && (
          <>
            <View style={styles.divider} />
            {!!diagnosis && (
              <View style={styles.notesSection}>
                <Text style={styles.notesSectionTitle}>Diagnose</Text>
                <ClampableText
                  numberOfLines={1}
                  expanded={isInfoCardExpanded}
                  style={styles.notesText}
                  onTruncatedChange={setIsDiagnosisTruncated}
                >
                  {diagnosis}
                </ClampableText>
              </View>
            )}
            {!!biography && (
              <View style={styles.notesSection}>
                <Text style={styles.notesSectionTitle}>Notizen</Text>
                <ClampableText
                  numberOfLines={2}
                  expanded={isInfoCardExpanded}
                  style={styles.notesText}
                  onTruncatedChange={setIsBiographyTruncated}
                >
                  {biography}
                </ClampableText>
              </View>
            )}
            {canExpand ? (
              <TouchableOpacity
                style={styles.expandButton}
                onPress={() => setIsInfoCardExpanded((prev) => !prev)}
              >
                <Text style={styles.expandButtonText}>
                  {isInfoCardExpanded ? 'einklappen' : 'ausklappen'}
                </Text>
                <Icon
                  name={isInfoCardExpanded ? 'chevronUp' : 'chevronDown'}
                  scale={0.8}
                  color={colors.newUI.therapist.primaryBlue60}
                />
              </TouchableOpacity>
            ) : null}
          </>
        )}
      </View>
    </>
  );
};
